import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useActiveMerchantId from "src/hooks/use-merchant-id";
import merchantProvider from "src/providers/merchant-provider";
import type { ListOrdersParams, Order, OrderParams } from "src/types";

const orderQueriers = {
	listOrders: (params?: ListOrdersParams) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: ["orders", merchantId ?? "no-merchant", params],
			queryFn: async () => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantProvider.listOrders(merchantId, {
					...params,
					limit: params?.limit ?? 20,
					offset: params?.offset ?? 0,
				});
			},
			enabled: Boolean(merchantId),
			retry: 2,
		});
	},

	getOrder: (orderId: string) => {
		const { merchantId } = useActiveMerchantId();
		return useQuery({
			queryKey: ["order", merchantId ?? "no-merchant", orderId],
			queryFn: async (): Promise<Order> => {
				if (!merchantId) {
					throw new Error("Query disabled: missing merchantId");
				}
				return merchantProvider.getOrder(merchantId, orderId);
			},
			enabled: Boolean(merchantId && orderId),
		});
	},

	createOrder: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: async (params: OrderParams): Promise<Order> => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return merchantProvider.createOrder(merchantId, params);
			},
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ["orders", merchantId ?? "no-merchant"],
				});
			},
		});
	},

	triggerOrderWebhook: () => {
		const { merchantId } = useActiveMerchantId();
		const queryClient = useQueryClient();

		return useMutation({
			mutationFn: async (
				orderId: string,
			): Promise<{ status: string; orderId: string }> => {
				if (!merchantId) {
					throw new Error("Missing merchantId");
				}
				return merchantProvider.triggerOrderWebhook(merchantId, orderId);
			},
			onSuccess: (_, orderId) => {
				// Invalidate the specific order and list
				queryClient.invalidateQueries({
					queryKey: ["order", merchantId ?? "no-merchant", orderId],
				});
				queryClient.invalidateQueries({
					queryKey: ["orders", merchantId ?? "no-merchant"],
				});
			},
		});
	},
};

export default orderQueriers;
